import { forwardRef, useMemo, useRef, useState } from 'react'
import {
  PickersLayout,
  type PickersLayoutProps,
} from '@mui/x-date-pickers/PickersLayout'
import { DesktopDateTimePickerLayout } from '@mui/x-date-pickers/DesktopDateTimePicker'
import type { PickerValue } from '@mui/x-date-pickers/internals'
import useMediaQuery from '@mui/material/useMediaQuery'
import { ThemeProvider, createTheme, useTheme } from '@mui/material/styles'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { heIL } from '@mui/x-date-pickers/locales'
import { he } from 'date-fns/locale/he'
import { useAppTheme } from '@/theme/ThemeContext'
import CalendarHeader from './CalendarHeader'
import DatePickerCalendarIcon from './DatePickerCalendarIcon'
import { dateErrorMessage, startOfDay } from './dateRange'
import {
  filterToolbarDatePickerPopupSx,
  filterToolbarDateSx,
} from './datePickerStyles'

const dateViews = ['year', 'month', 'day']
const timeViews = [...dateViews, 'hours', 'minutes']

const localeText = {
  ...heIL.components.MuiLocalizationProvider.defaultProps.localeText,
  fieldDayPlaceholder: () => 'dd',
  fieldMonthPlaceholder: () => 'mm',
  fieldYearPlaceholder: () => 'yyyy',
  fieldHoursPlaceholder: () => 'hh',
  fieldMinutesPlaceholder: () => 'mm',
}

// The calendar opens in a portal, outside the page's RTL boxes. A theme with
// direction rtl, given to the calendar only, flips the month arrows. On the
// field it would add bidi marks that reorder the date and time.
const rtlLayout = (Layout: typeof PickersLayout) =>
  forwardRef<HTMLDivElement, PickersLayoutProps<PickerValue>>(
    function RtlPickersLayout(props, ref) {
      const outer = useTheme()
      const theme = useMemo(() => createTheme(outer, { direction: 'rtl' }), [outer])
      return (
        <ThemeProvider theme={theme}>
          <Layout {...props} ref={ref} />
        </ThemeProvider>
      )
    }
  )

const RtlPickersLayout = rtlLayout(PickersLayout)
// The date and time calendar puts the days and the time lists side by side in a grid. The
// plain layout stacks them in a flex row and the calendar collapses to no height.
const RtlDesktopDateTimeLayout = rtlLayout(
  DesktopDateTimePickerLayout as unknown as typeof PickersLayout
)

// When the calendar fits neither below nor above the field, popper leaves it
// hanging below the window. Opening the month or year list then focuses an item
// past the fold and the browser scrolls the whole page. Keeping the calendar
// inside the window on both axes leaves nothing to scroll to.
const keepInWindow = [
  { name: 'preventOverflow', options: { altAxis: true, padding: 8 } },
]

export type AppDatePickerProps = {
  label: string
  value: Date | null
  onChange: (value: Date | null) => void
  minDate?: Date
  maxDate?: Date
  disableFuture?: boolean
  /** Adds a clear button to the calendar, for optional dates. */
  clearable?: boolean
  /** Asks for the time of day as well, dd/mm/yyyy HH:mm. */
  withTime?: boolean
  disabled?: boolean
  /** Fills the field like the text fields of a filter toolbar. */
  filled?: boolean
  /** Paints the field red, for an overdue date. */
  danger?: boolean
  format?: string
  /** Any CSS width, or '100%' to fill the parent. */
  width?: string
  /**
   * Fires when the user is done with the field: the calendar closed after a
   * pick, the today or clear button, or the field lost focus after typing. `onChange` also fires on
   * every typed digit, so a caller that saves the value does it here.
   */
  onCommit?: (value: Date | null) => void
  /** Gets the short Hebrew message, or null once the typed date is fine. */
  onErrorChange?: (message: string | null) => void
}

const isUsableDate = (value: Date | null): value is Date =>
  value instanceof Date && !Number.isNaN(value.getTime())

// A year under 1000 is a year still being typed, not a date to act on.
const isCommittable = (value: Date | null) =>
  value === null || (isUsableDate(value) && value.getFullYear() >= 1000)

export default function AppDatePicker({
  label,
  value,
  onChange,
  minDate,
  maxDate,
  disableFuture,
  clearable = false,
  withTime = false,
  disabled,
  filled = false,
  danger = false,
  format,
  width = 'clamp(150px, 12vw, 210px)',
  onCommit,
  onErrorChange,
}: AppDatePickerProps) {
  const { mode } = useAppTheme()
  const isDarkMode = mode === 'dark'
  const [message, setMessage] = useState<string | null>(null)
  const latest = useRef(value)
  latest.current = value
  const Picker = withTime ? DateTimePicker : DatePicker
  const actions = clearable
    ? (['clear', 'today'] as const)
    : (['today'] as const)
  // The same query MUI uses to pick the desktop popup over the phone dialog.
  const desktop = useMediaQuery('@media (pointer: fine)')
  const layout =
    withTime && desktop ? RtlDesktopDateTimeLayout : RtlPickersLayout
  const popupSx = filterToolbarDatePickerPopupSx(isDarkMode)
  const paper = { sx: popupSx, dir: 'rtl' as const }

  return (
    <LocalizationProvider
      dateAdapter={AdapterDateFns}
      adapterLocale={he}
      localeText={localeText}
      dateFormats={{ monthShort: 'LLLL' }}
    >
      <Picker
        label={label}
        value={value as never}
        disabled={disabled}
        minDate={minDate as never}
        maxDate={maxDate as never}
        disableFuture={disableFuture}
        format={format ?? (withTime ? 'dd/MM/yyyy HH:mm' : 'dd/MM/yyyy')}
        views={(withTime ? timeViews : dateViews) as never}
        {...(withTime ? { ampm: false } : {})}
        onChange={picked => {
          const next = picked as unknown as Date | null
          if (next !== null && !isUsableDate(next)) return
          const day = next && !withTime ? startOfDay(next) : next
          latest.current = day
          onChange(day)
        }}
        onClose={() => {
          if (isCommittable(latest.current)) onCommit?.(latest.current)
        }}
        onError={error => {
          const next = dateErrorMessage(error)
          setMessage(next)
          onErrorChange?.(next)
        }}
        slots={{
          openPickerIcon: DatePickerCalendarIcon,
          layout,
          calendarHeader: CalendarHeader,
        }}
        slotProps={{
          textField: {
            size: 'small',
            error: message !== null,
            helperText: message,
            InputLabelProps: { shrink: true },
            onBlur: () => {
              if (isCommittable(latest.current)) onCommit?.(latest.current)
            },
            sx: {
              width,
              minWidth: width,
              ...filterToolbarDateSx(isDarkMode, value !== null, filled),
              ...(danger && {
                '--filter-outline': isDarkMode ? '#FF9B8A' : '#FE595A',
                '& .MuiPickersSectionList-root': {
                  color: isDarkMode ? '#FF9B8A' : '#FE595A',
                },
              }),
              '& .MuiFormHelperText-root': {
                position: 'absolute',
                top: '100%',
                right: 0,
                m: '2px 4px 0',
                whiteSpace: 'nowrap',
                fontFamily: 'Rubik, sans-serif',
                color: isDarkMode ? '#FF9B8A' : '#C62828',
              },
            },
          },
          actionBar: { actions: [...actions] },
          popper: { modifiers: keepInWindow },
          desktopPaper: paper,
          mobilePaper: paper,
        }}
      />
    </LocalizationProvider>
  )
}
